import React from "react";
import { create } from "zustand";
import { HeightReference } from "cesium";
import { createListCollection } from "@chakra-ui/react";

import Type from "../utilies/Types";

// -----------------------
// Helpers
// -----------------------
const getFromCache = (key) => {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

const createUniqueId = () => "id-" + Math.random().toString(36).substr(2, 9);

const resolveUpdater = (updater, prev) => (typeof updater === "function" ? updater(prev) : updater);

// -----------------------
// Defaults
// -----------------------
const DEFAULT_GEOJSON_STYLE = () => ({
  defaultLineWidth: 2,
  defaultStyle: "Solid",
  defaultFillColor: "#FFFFFF",
  defaultOutlineColor: "#000000",
  clampToGround: true,
  heightReference: HeightReference.NONE,
  dataSourceName: "geojson-" + createUniqueId(),
  solidDashLength: 0.0,
  dashedDashLength: 16.0,
  dottedDashLength: 5.0,
  dashPattern: 255,
  zoomToDataSource: true,
});

const DEFAULT_GEOJSON_IMAGE_CAMERA = {
  1: true,
  2: true,
  3: true,
  4: true,
  5: true,
  6: true,
  7: true,
  8: true,
};

const DEFAULT_CESIUM_SETTING = {
  map_view: Type.SCENE.SCENE_2D,
};

const DEFAULT_TOOLS_ENABLED = {
  measureDistance: true,
  lineIntersectMeasure: true,
  quickSearch: true,
  showPoints: true,
  validationMenu: true,
  globalTools: true,
  overlayText: true,
  trafficLight: true,
  trafficSign: true,
  lanelet: true,
  LaneletCheckByIds: true,
  AutomaticConnectionCheck: true,
  association: true,
  arc: true,
  generateRoute: true,
  pointCloud: true,
  roadMarking: true,
  autoSketcher: true,
  measureArea: true,
  driveway: true,
  lanemarkerTool: true,
  movingTool: true,
  turningArcTool: true,
  laneConnector: true,
};

const cachedToolsEnabled = getFromCache("toolsEnabled");
const initialToolsEnabled = {
  ...DEFAULT_TOOLS_ENABLED,
  ...(cachedToolsEnabled || {}),
};

// -----------------------
// Refs / shared singletons
// -----------------------
const trafficSignModeRef = { current: false };
const trafficLightModeRef = { current: false };
const generateRouteModeRef = { current: false };
const drivewayModeRef = { current: false };

const selectedEntitiesRef = { current: [] };
const appModeRef = { current: Type.MODE.VIEW };
const createLaneletModeRef = { current: false };

const isEnableSelectingCommentRef = { current: false };
const autoSelectRef = { current: false };
const associationRef = { current: false };

const mapObjectsAssestRef = { current: [] };

const snappingOptionsRef = { current: { vertex: true, midpoint: true } };
const editModeRef = { current: false };
const editTypeRef = { current: "none" };
const showQueryTableDialogRef = { current: false };

const featurePointMapping = new Map();
const controlPointEntities = [];

const cursorModeRef = { current: Type.CURSOR.DEFAULT };

const roadMarkingModeRef = { current: false };
const roadMarkingMovingModeRef = { current: false };

const autoSketcherModeRef = { current: false };

const undoStackRef = { current: [] };

const directionArrowSettingRef = {
  current: {
    color: "#000000",
    scale: 0.17,
  },
};

const simplifyToleranceSettingRef = {
  current: {
    distance: 50,
    azimuth: 0.1,
    generate_distance: 5,
    rearrange: 5,
    find_break_points: 160,
    find_close_point: 0.05,
  },
};

const lineSampleRateRef = { current: 0 };

const canOpenInfoboxRef = { current: true };
const canHightlightEntityRef = { current: true };

const generateArcRef = { current: false };

const globalUndoStackRef = { current: [] };

const overlayTextHandlerRef = { current: null };
const highlightToolsRef = { current: null };

const loadingDataForLayers = {
  current: {
    current: 0,
    total: 0,
    show: false,
  },
};

const settingsDefaultValues = {
  editing: {
    shiftSelectionRadius: 3,
  },
  general: {
    laneConnectorSearchRadiusMeters: 40, // meter
    laneConnectorAngleDeg: 7,
  },
};

// -----------------------
// Hotkeys
// -----------------------
const tempHotkeys = [
  // --- General ---
  {
    id: "edit-layer",
    group: "general",
    type: Type.HOTKEYS.ACTIVE_EDIT_LAYER,
    title: "Edit Layer Panel",
    desc: "Open the active layer editing panel.",
    combination: ["shift", "t"],
  },
  {
    id: "measure-distance",
    group: "general",
    type: Type.HOTKEYS.MEASURE_DISTANCE,
    title: "Measure Distance",
    desc: "Measure distance on the map.",
    combination: ["shift", "m"],
  },
  {
    id: "save-layer",
    group: "general",
    type: Type.HOTKEYS.SAVE_LAYER_TO_SERVER,
    title: "Save Layer to Server",
    desc: "Upload the active layer to the server.",
    combination: ["shift", "g"],
  },
  {
    id: "delete-layer",
    group: "general",
    type: Type.HOTKEYS.DELETE_LAYER,
    title: "Delete Layer",
    desc: "Remove the selected layer from the project.",
    combination: ["shift", "k"],
  },
  {
    id: "toggle-layer",
    group: "general",
    type: Type.HOTKEYS.TOGGLE_LAYER_VISIBILITY,
    title: "Toggle Layer Visibility",
    desc: "Show/Hide the selected layer.",
    combination: ["shift", "y"],
  },
  {
    id: "toggle-labels",
    group: "general",
    type: Type.HOTKEYS.TOGGLE_LABEL_VISIBILITY,
    title: "Toggle Labels",
    desc: "Show/Hide labels in the active layer.",
    combination: ["shift", "n"],
  },
  {
    id: "zoom-extent",
    group: "general",
    type: Type.HOTKEYS.ZOOM_TO_EXTENT,
    title: "Zoom to Extent",
    desc: "Fit view to the active layer content.",
    combination: ["shift", "o"],
  },
  {
    id: "save-db",
    group: "general",
    type: Type.HOTKEYS.SAVE_TO_DATABASE,
    title: "Save to Database",
    desc: "Save current edits to the database.",
    combination: ["shift", "ctrl", "s"],
  },
  {
    id: "save-project",
    group: "general",
    type: Type.HOTKEYS.SAVE_PROJECT,
    title: "Save Project",
    desc: "Save the current project.",
    combination: ["shift", "p"],
  },
  {
    id: "toggle-some-visibility",
    group: "general",
    type: Type.HOTKEYS.TOGGLE_SOME_VISIBILITY,
    title: "Toggle Some Layers",
    desc: "Show/Hide a predefined layer group.",
    combination: ["shift", "b"],
  },
  {
    id: "toggle-route-visibility",
    group: "general",
    type: Type.HOTKEYS.TOGGLE_ROUTE_LAYERS_VISIBILITY,
    title: "Toggle Route Layers",
    desc: "Show/Hide route-related layers.",
    combination: ["shift", "r"],
  },
  {
    id: "open-search-geosjon",
    group: "general",
    type: Type.HOTKEYS.OPEN_SEARCH_GEOJSON,
    title: "Open GeoJSON Search",
    desc: "Open the GeoJSON search panel.",
    combination: ["shift", "e"],
  },
  {
    id: "reset-search-geosjon",
    group: "general",
    type: Type.HOTKEYS.RESET_SEARCH_GEOJSON,
    title: "Reset GeoJSON Search",
    desc: "Clear GeoJSON search filters.",
    combination: ["shift", "ctrl", "e"],
  },

  // --- Switch ---
  {
    id: "toggle-lane-marker-edit",
    group: "switch",
    type: Type.HOTKEYS.TOGGLE_LANE_MARKER_EDIT,
    title: "Toggle Lane Marker Edit",
    desc: "Toggle edit mode for LANE_MARKER layer.",
    combination: ["ctrl", "l", "m"],
  },
  {
    id: "toggle-virtual-layer-edit",
    group: "switch",
    type: Type.HOTKEYS.TOGGLE_VIRTUAL_LAYER_EDIT,
    title: "Toggle Virtual Layer Edit",
    desc: "Toggle edit mode for VIRTUAL layer.",
    combination: ["ctrl", "l", "v"],
  },
  {
    id: "toggle-road-edge-edit",
    group: "switch",
    type: Type.HOTKEYS.TOGGLE_ROAD_EDGE_EDIT,
    title: "Toggle Road Edge Edit",
    desc: "Toggle edit mode for ROAD_EDGE layer.",
    combination: ["ctrl", "l", "r"],
  },
  {
    id: "toggle-traffic-sign-box-edit",
    group: "switch",
    type: Type.HOTKEYS.TOGGLE_TRAFFIC_SIGN_BOX_EDIT,
    title: "Toggle Traffic Sign Box Edit",
    desc: "Toggle edit mode for TRAFFIC_SIGN_BOX layer.",
    combination: ["ctrl", "l", "b"],
  },
  {
    id: "toggle-stop-line-edit",
    group: "switch",
    type: Type.HOTKEYS.TOGGLE_STOP_LINE_EDIT,
    title: "Toggle Stop Line Edit",
    desc: "Toggle edit mode for STOP_LINE layer.",
    combination: ["ctrl", "l", "s"],
  },
  {
    id: "toggle-curbstone-edit",
    group: "switch",
    type: Type.HOTKEYS.TOGGLE_CURBSTONE_EDIT,
    title: "Toggle Curbstone Edit",
    desc: "Toggle edit mode for CURBSTONE layer.",
    combination: ["ctrl", "l", "c"],
  },

  // --- Geometry ---
  {
    id: "create-lanelet",
    group: "geometry",
    type: Type.HOTKEYS.CREATE_LANELET,
    title: "Create Lanelet",
    desc: "Start drawing a new lanelet.",
    combination: ["ctrl", "."],
  },
  {
    id: "select-lanelet",
    group: "general",
    type: Type.HOTKEYS.SELECT_LANELET,
    title: "Select Lanelet",
    desc: "Enable lanelet selection mode.",
    combination: ["ctrl", "/"],
  },
  {
    id: "toggle-direction",
    group: "general",
    type: Type.HOTKEYS.TOGGLE_DIRECTION_VISIBILITY,
    title: "Toggle Directions",
    desc: "Show/Hide direction arrows.",
    combination: ["shift", "u"],
  },
  {
    id: "toggle-association",
    group: "geometry",
    type: Type.HOTKEYS.TOGGLE_ASSOCIATION,
    title: "Toggle Associations",
    desc: "Show/Hide associated features.",
    combination: ["shift", "a"],
  },
  {
    id: "toggle-association-fast",
    group: "geometry",
    type: Type.HOTKEYS.TOGGLE_ASSOCIATION_FAST,
    title: "Toggle Auto Associations",
    desc: "Enable/Disable auto association display.",
    combination: ["shift", "h"],
  },
  {
    id: "line-intersect-measure",
    group: "geometry",
    type: Type.HOTKEYS.LINE_INTERSECT_MEASURE,
    title: "Measure Intersections",
    desc: "Measure intersection points of lines.",
    combination: ["shift", "l"],
  },
  {
    id: "auto-sketcher",
    group: "geometry",
    type: Type.HOTKEYS.AUTOSKETCHER,
    title: "Auto Sketcher",
    desc: "Auto-generate a sketch from inputs.",
    combination: ["ctrl", "b"],
  },
  {
    id: "driveway",
    group: "geometry",
    type: Type.HOTKEYS.DRIVEWAY,
    title: "Driveway",
    desc: "Create driveway geometry.",
    combination: ["ctrl", "d", "r"],
  },

  {
    id: "rectangle",
    group: "tools",
    type: Type.HOTKEYS.SELECT_RECTANGLE,
    title: "Rectangle Select",
    desc: "Select features using a rectangle.",
    combination: ["shift", "s"],
  },
  {
    id: "split",
    group: "tools",
    type: Type.HOTKEYS.SPLIT,
    title: "Split",
    desc: "Split selected geometry into two.",
    combination: ["shift", "x"],
  },
  {
    id: "split-auto",
    group: "tools",
    type: Type.HOTKEYS.SPLIT_AUTO,
    title: "Auto Split",
    desc: "Split geometry automatically.",
    combination: ["shift", "x", "a"],
  },
  {
    id: "auto-snap-cluster",
    group: "tools",
    type: Type.HOTKEYS.AUTO_SNAP_CLUSTER,
    title: "Auto Snap Cluster",
    desc: "Snap automatically.",
    combination: ["shift", "ctrl", "m"],
  },
  {
    id: "join-lines",
    group: "editing",
    type: Type.HOTKEYS.JOIN_LINES,
    title: "Join Lines",
    desc: "Join two selected lines.",
    combination: ["space", "j"],
  },
  {
    id: "spline",
    group: "editing",
    type: Type.HOTKEYS.SPLINE,
    title: "Spline",
    desc: "Draw a smooth curved line.",
    combination: ["space", "p"],
  },
  {
    id: "parallel",
    group: "editing",
    type: Type.HOTKEYS.PARALLEL,
    title: "Parallel",
    desc: "Create a parallel copy of a line.",
    combination: ["space", "l"],
  },

  // --- Editing ---
  {
    id: "align-endpoints",
    group: "tools",
    type: Type.HOTKEYS.ALIGN_ENDPOINTS,
    title: "Align Endpoints",
    desc: "Align endpoints to a reference line.",
    combination: ["shift", "ctrl", "a"],
  },

  {
    id: "save-editing",
    group: "editing",
    type: Type.HOTKEYS.SAVE_EDITING,
    title: "Save Editing",
    desc: "Save current edits locally.",
    combination: ["space", "s"],
  },
  {
    id: "delete-selected",
    group: "editing",
    type: Type.HOTKEYS.DELETE_SELECTED,
    title: "Delete Selected",
    desc: "Delete selected features.",
    combination: ["space", "d"],
  },
  {
    id: "reverse-waypoints",
    group: "editing",
    type: Type.HOTKEYS.REVERSE_WAYPOINTS,
    title: "Reverse Waypoints",
    desc: "Reverse waypoint order.",
    combination: ["space", "r"],
  },
  {
    id: "split-edit",
    group: "editing",
    type: Type.HOTKEYS.SPLITEDIT,
    title: "Split Edit",
    desc: "Split a feature at a chosen point.",
    combination: ["space", "c"],
  },
  {
    id: "add-new-feature",
    group: "editing",
    type: Type.HOTKEYS.ADD_NEW_FEATURE,
    title: "Add New Feature",
    desc: "Add a new feature to the layer.",
    combination: ["space", "n"],
  },
  {
    id: "undo-edit",
    group: "editing",
    type: Type.HOTKEYS.UNDOEDIT,
    title: "Undo Edit",
    desc: "Undo the last edit action.",
    combination: ["space", "z"],
  },
  {
    id: "redo",
    group: "editing",
    type: Type.HOTKEYS.REDO,
    title: "Redo",
    desc: "Redo the last undone action.",
    combination: ["space", "x"],
  },
  {
    id: "cancel-editing",
    group: "editing",
    type: Type.HOTKEYS.CANCEL_EDITING,
    title: "Cancel Editing",
    desc: "Exit editing mode without saving.",
    combination: ["space", "escape"],
  },

  // --- Navigation ---
  { id: "pan-up", group: "navigation", type: Type.HOTKEYS.PAN_UP, title: "Pan Up", desc: "Move camera up.", combination: ["arrowup"] },
  { id: "pan-down", group: "navigation", type: Type.HOTKEYS.PAN_DOWN, title: "Pan Down", desc: "Move camera down.", combination: ["arrowdown"] },
  { id: "pan-left", group: "navigation", type: Type.HOTKEYS.PAN_LEFT, title: "Pan Left", desc: "Move camera left.", combination: ["arrowleft"] },
  { id: "pan-right", group: "navigation", type: Type.HOTKEYS.PAN_RIGHT, title: "Pan Right", desc: "Move camera right.", combination: ["arrowright"] },

  {
    id: "bg-move-up",
    group: "navigation",
    type: Type.HOTKEYS.BG_MOVE_UP,
    title: "Background Up",
    desc: "Move background image up slightly.",
    combination: ["shift", "b", "arrowup"],
  },
  {
    id: "bg-move-down",
    group: "navigation",
    type: Type.HOTKEYS.BG_MOVE_DOWN,
    title: "Background Down",
    desc: "Move background image down slightly.",
    combination: ["shift", "b", "arrowdown"],
  },
  { id: "bg-move-left", group: "navigation", type: Type.HOTKEYS.BG_MOVE_LEFT, title: "Background Left", desc: "Move background image left.", combination: ["shift", "b", "arrowleft"] },
  {
    id: "bg-move-right",
    group: "navigation",
    type: Type.HOTKEYS.BG_MOVE_RIGHT,
    title: "Background Right",
    desc: "Move background image right.",
    combination: ["shift", "b", "arrowright"],
  },

  { id: "zoom-in", group: "navigation", type: Type.HOTKEYS.ZOOM_IN, title: "Zoom In", desc: "Zoom in slightly.", combination: ["shift", "="] },
  { id: "zoom-out", group: "navigation", type: Type.HOTKEYS.ZOOM_OUT, title: "Zoom Out", desc: "Zoom out slightly.", combination: ["shift", "-"] },

  { id: "move-forward", group: "navigation", type: Type.HOTKEYS.MOVE_FORWARD, title: "Move Forward", desc: "Move camera forward.", combination: ["ctrl", "arrowup"] },
  { id: "move-backward", group: "navigation", type: Type.HOTKEYS.MOVE_BACKWARD, title: "Move Backward", desc: "Move camera backward.", combination: ["ctrl", "arrowdown"] },
  { id: "move-left", group: "navigation", type: Type.HOTKEYS.MOVE_LEFT, title: "Move Left", desc: "Move camera left.", combination: ["ctrl", "arrowleft"] },
  { id: "move-right", group: "navigation", type: Type.HOTKEYS.MOVE_RIGHT, title: "Move Right", desc: "Move camera right.", combination: ["ctrl", "arrowright"] },

  { id: "height-up", group: "navigation", type: Type.HOTKEYS.HEIGHT_UP, title: "Increase Height", desc: "Increase camera height.", combination: ["shift", "ctrl", "arrowup"] },
  { id: "height-down", group: "navigation", type: Type.HOTKEYS.HEIGHT_DOWN, title: "Decrease Height", desc: "Decrease camera height.", combination: ["shift", "ctrl", "arrowdown"] },

  { id: "rotate-up", group: "navigation", type: Type.HOTKEYS.ROTATE_UP, title: "Tilt Up", desc: "Tilt camera up.", combination: ["shift", "arrowup"] },
  { id: "rotate-down", group: "navigation", type: Type.HOTKEYS.ROTATE_DOWN, title: "Tilt Down", desc: "Tilt camera down.", combination: ["shift", "arrowdown"] },
  { id: "rotate-left", group: "navigation", type: Type.HOTKEYS.ROTATE_LEFT, title: "Rotate Left", desc: "Rotate camera left.", combination: ["shift", "arrowleft"] },
  { id: "rotate-right", group: "navigation", type: Type.HOTKEYS.ROTATE_RIGHT, title: "Rotate Right", desc: "Rotate camera right.", combination: ["shift", "arrowright"] },

  // --- Tools ---
  {
    id: "intersection-points",
    group: "tools",
    type: Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS,
    title: "Intersection Points",
    desc: "Show some intersection points.",
    combination: ["shift", "w"],
  },
  {
    id: "intersection-points-all",
    group: "tools",
    type: Type.HOTKEYS.TOPOLOGY_INTERSECTION_POINTS_ALL,
    title: "Intersection Points All",
    desc: "Show all intersection points.",
    combination: ["shift", "w", "a"],
  },

  { id: "dangling-lines", group: "tools", type: Type.HOTKEYS.TOPOLOGY_DANGLING_LINES, title: "Dangling Lines", desc: "Highlight unconnected line ends.", combination: ["shift", "f"] },
  { id: "auto-snap", group: "tools", type: Type.HOTKEYS.TOGGLE_AUTO_SNAP, title: "Auto Snap", desc: "Enable/Disable snapping.", combination: ["shift", "q"] },
  { id: "undo", group: "tools", type: Type.HOTKEYS.UNDO, title: "Undo", desc: "Undo last operation.", combination: ["shift", "z"] },
  { id: "delete", group: "tools", type: Type.HOTKEYS.DELETE, title: "Delete", desc: "Delete selected features.", combination: ["shift", "d"] },
  { id: "auto-select", group: "tools", type: Type.HOTKEYS.AUTO_SELECT, title: "Auto Select", desc: "Auto-select features.", combination: ["shift", "1"] },
  { id: "deselect", group: "tools", type: Type.HOTKEYS.DESELECT, title: "Deselect", desc: "Clear current selection.", combination: ["shift", "2"] },
  { id: "switch-lanelet-sides", group: "tools", type: Type.HOTKEYS.SWITCH_LANELET_SIDES, title: "Switch Lanelet Sides", desc: "Switch Selected Lanelet Sides.", combination: ["shift", "4"] },
  {
    id: "switch-lanelet-turn-direction",
    group: "tools",
    type: Type.HOTKEYS.SWITCH_LANELET_TURN_DIRECTION,
    title: "Switch Lanelet Turn Direction",
    desc: "Switch Selected Lanelet Turn Direction.",
    combination: ["shift", "5"],
  },
];

const defaultHotkeysRef = {
  current: tempHotkeys.map((h) => ({ ...h, combination: [...h.combination] })),
};

let initialHotkeys = tempHotkeys;

const shortcuts = getFromCache("shortcuts");
if (shortcuts) {
  try {
    const cached = JSON.parse(shortcuts);
    const cacheMap = new Map(cached.map((hk) => [hk.id, hk.combination]));
    initialHotkeys = tempHotkeys.map((hk) => {
      const combo = cacheMap.get(hk.id);
      return combo ? { ...hk, combination: combo } : hk;
    });
  } catch {
    console.warn("Invalid shortcuts cache");
  }
}

const savedHotkeysRef = { current: initialHotkeys };

// -----------------------
// Import type options
// -----------------------
const importTypeOptions = createListCollection({
  items: [
    { name: "3D Tiles", id: Type.ASSET.THREEDTILES },
    { name: "Camera Image", id: Type.ASSET.GEOJSON_IMAGE },
    { name: "Geojson", id: Type.ASSET.GEOJSON },
    { name: "OSM", id: Type.ASSET.OSM },
    { name: "Zip", id: Type.ASSET.ZIP },
    { name: "GLB", id: Type.ASSET.GLB },
    { name: "CSV", id: Type.ASSET.CSV },
    { name: "KML", id: Type.ASSET.KML },
    { name: "KMZ", id: Type.ASSET.KMZ },
  ],
  itemToString: (item) => item.name,
  itemToValue: (item) => item.id,
});

// -----------------------
// Store
// -----------------------
export const useAppStore = create((set, get) => {
  const setField = (key) => (updater) =>
    set((state) => ({
      [key]: resolveUpdater(updater, state[key]),
    }));

  const EXCLUSIVE_MODE_KEYS = [
    "generateArc",
    "association",
    "fastAssociation",
    "createLaneletMode",
    "checkLaneletMode",
    "measureDistanceMode",
    "measureAreaMode",
    "lineIntersectMeasureMode",
    "overlayTextMode",
    "trafficSignMode",
    "trafficLightMode",
    "generateRouteMode",
    "roadMarkingMode",
    "roadMarkingMovingMode",
    "autoSketcherMode",
    "drivewayMode",
    "moveToolMode",
    "laneMarkerStyleMode",
    "turningArcMode",
    "globalMode",
  ];

  const EXCLUSIVE_MODE_REFS = {
    generateArc: generateArcRef,
    association: associationRef,
    createLaneletMode: createLaneletModeRef,
    trafficSignMode: trafficSignModeRef,
    trafficLightMode: trafficLightModeRef,
    generateRouteMode: generateRouteModeRef,
    roadMarkingMode: roadMarkingModeRef,
    roadMarkingMovingMode: roadMarkingMovingModeRef,
    autoSketcherMode: autoSketcherModeRef,
    drivewayMode: drivewayModeRef,
  };

  const turnOffExclusiveModesIfEnabled = (nextValue, keepKey) => {
    if (!nextValue) return {};
    const patch = {};
    for (const k of EXCLUSIVE_MODE_KEYS) {
      if (k !== keepKey) {
        if (k === "globalMode") {
          if (get().globalMode === "split") patch.globalMode = "none";
        } else {
          patch[k] = false;
          const r = EXCLUSIVE_MODE_REFS[k];
          if (r && typeof r === "object" && "current" in r) r.current = false;
        }
      }
    }
    return patch;
  };

  return {
    // -----------------------
    // Auth
    // -----------------------
    isLogedIn: "loading",
    setIsLoggedIn: setField("isLogedIn"),

    username: "",
    setUsername: setField("username"),

    password: "",
    setPassword: setField("password"),

    userType: [],
    setUserType: setField("userType"),

    userData: null,
    setUserData: setField("userData"),

    // -----------------------
    // Selected entities
    // -----------------------
    selectedEntities: [],
    selectedEntitiesRef,
    setSelectedEntities: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.selectedEntities);
        selectedEntitiesRef.current = nextValue;
        return { selectedEntities: nextValue };
      }),

    // -----------------------
    // App mode
    // -----------------------
    appMode: Type.MODE.VIEW,
    appModeRef,
    setAppMode: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.appMode);
        appModeRef.current = nextValue;
        return { appMode: nextValue };
      }),

    // -----------------------
    // Grid
    // -----------------------
    gridMode: false,
    setGridMode: setField("gridMode"),

    gridSize: [120],
    setGridSize: setField("gridSize"),

    gridColor: "#000000",
    setGridColor: setField("gridColor"),

    isOpenEditCoordianteModal: false,
    setIsOpenEditCoordianteModal: setField("isOpenEditCoordianteModal"),

    editCoordianteModalData: null,
    setEditCoordianteModalData: setField("editCoordianteModalData"),

    // -----------------------
    // Lanelet button / mode
    // -----------------------
    showLaneletButton: false,
    setShowLaneletButton: setField("showLaneletButton"),

    moveToolMode: false,
    setMoveToolMode: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.moveToolMode);

        return {
          moveToolMode: Boolean(nextValue),
          ...turnOffExclusiveModesIfEnabled(nextValue, "moveToolMode"),
        };
      }),

    laneMarkerStyleMode: false,
    setLaneMarkerStyleMode: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.laneMarkerStyleMode);

        return {
          laneMarkerStyleMode: Boolean(nextValue),
          ...turnOffExclusiveModesIfEnabled(nextValue, "laneMarkerStyleMode"),
        };
      }),

    createLaneletMode: false,
    createLaneletModeRef,
    setCreateLaneletMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.createLaneletMode));
        createLaneletModeRef.current = nextValue;
        return {
          createLaneletMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "createLaneletMode"),
        };
      }),

    checkLaneletMode: false,
    setCheckLaneletMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.checkLaneletMode));
        return {
          checkLaneletMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "checkLaneletMode"),
        };
      }),

    isActiveChecker: false,
    setIsActiveChecker: setField("isActiveChecker"),

    centerLineLastFeature: null,
    setCenterLineLastFeature: setField("centerLineLastFeature"),

    // -----------------------
    // Image geojson
    // -----------------------
    imageGeojsonMode: false,
    setImageGeojsonMode: setField("imageGeojsonMode"),

    imageGeojson: null,
    setImageGeojson: setField("imageGeojson"),

    imageGeojsonSelected: null,
    setImageGeojsonSelected: setField("imageGeojsonSelected"),

    imageDataSource: null,
    setImageDataSource: setField("imageDataSource"),

    isEnableSelectingComment: false,
    isEnableSelectingCommentRef,
    setIsEnableSelectingComment: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.isEnableSelectingComment);
        isEnableSelectingCommentRef.current = nextValue;
        return { isEnableSelectingComment: nextValue };
      }),

    showCommentDialog: false,
    setShowCommentDialog: setField("showCommentDialog"),

    showLoadGeneratedOsmDialog: null,
    setShowLoadGeneratedOsmDialog: setField("showLoadGeneratedOsmDialog"),

    commentSendingData: {},
    setCommentSendingData: setField("commentSendingData"),

    autoSelect: false,
    autoSelectRef,
    setAutoSelect: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.autoSelect);
        autoSelectRef.current = nextValue;
        return { autoSelect: nextValue };
      }),

    association: false,
    associationRef,
    setAssociation: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.association));
        associationRef.current = nextValue;
        return {
          association: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "association"),
        };
      }),

    imageGeojsonCounter: 0,
    setImageGeojsonCounter: setField("imageGeojsonCounter"),

    geojsonImageCamera: { ...DEFAULT_GEOJSON_IMAGE_CAMERA },
    setGeojsonImageCamera: setField("geojsonImageCamera"),

    // -----------------------
    // Map / viewer
    // -----------------------
    mapStyle: "Bing Maps Aerial",
    setMapStyle: setField("mapStyle"),

    viewerRef: { current: null },
    cesiumContainer: { current: null },

    cesiumSetting: { ...DEFAULT_CESIUM_SETTING },
    setCesiumSetting: setField("cesiumSetting"),

    originalCameraControls: {
      enableRotate: true,
      enableTranslate: true,
      enableZoom: true,
      enableTilt: true,
      enableLook: true,
    },

    // -----------------------
    // Styles
    // -----------------------
    defaultGeojsonStyle: { ...DEFAULT_GEOJSON_STYLE() },
    setDefaultGeojsonStyle: setField("defaultGeojsonStyle"),

    defaultOsmStyle: {},
    setDefaultOsmStyle: setField("defaultOsmStyle"),

    mapObjectsAssest: [],
    mapObjectsAssestRef,
    setMapObjectsAssest: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.mapObjectsAssest);
        mapObjectsAssestRef.current = nextValue;
        return { mapObjectsAssest: nextValue };
      }),

    activeLayer: null,
    setActiveLayer: setField("activeLayer"),

    currentLayerEditing: null,
    setCurrentLayerEditing: setField("currentLayerEditing"),

    currentLayer: null,
    setCurrentLayer: setField("currentLayer"),

    currentLayerForJsonEditor: null,
    setCurrentLayerForJsonEditor: setField("currentLayerForJsonEditor"),

    currentLayerForAttributeTable: null,
    setCurrentLayerForAttributeTable: setField("currentLayerForAttributeTable"),

    // -----------------------
    // Global panels
    // -----------------------
    isOpenReviewerPanel: false,
    globalToolsOpen: false,

    setIsOpenReviewerPanel: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.isOpenReviewerPanel);
        return {
          isOpenReviewerPanel: nextValue,
          ...(nextValue
            ? {
                globalToolsOpen: false,
                isUserManagementOpen: false,
                isAdminTaskListOpen: false,
                isOpenAddonsMenu: false,
              }
            : {}),
        };
      }),

    setGlobalToolsOpen: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.globalToolsOpen);
        return {
          globalToolsOpen: nextValue,
          ...(nextValue ? { isOpenReviewerPanel: false } : {}),
        };
      }),

    // -----------------------
    // Measure tools
    // -----------------------
    measureDistanceMode: false,
    setMeasureDistanceMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.measureDistanceMode));

        return {
          measureDistanceMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "measureDistanceMode"),
        };
      }),

    measureAreaMode: false,
    setMeasureAreaMode: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.measureAreaMode);

        return {
          measureAreaMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "measureAreaMode"),
        };
      }),

    snappingOptions: { vertex: true, midpoint: true },
    snappingOptionsRef,
    setSnappingOptions: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.snappingOptions);
        snappingOptionsRef.current = nextValue;
        return { snappingOptions: nextValue };
      }),

    // -----------------------
    // Edit tools
    // -----------------------
    editMode: false,
    editModeRef,
    setEditMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.editMode));
        editModeRef.current = nextValue;

        return {
          editMode: nextValue,
          ...(nextValue
            ? {
                ...turnOffExclusiveModesIfEnabled(true, "editMode"),
                globalMode: "none",
              }
            : {}),
        };
      }),

    editType: "none",
    editTypeRef,
    setEditType: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.editType);
        editTypeRef.current = nextValue;
        return { editType: nextValue };
      }),

    editHandler: [],
    setEditHandler: setField("editHandler"),

    // -----------------------
    // Dialogs
    // -----------------------
    isOpenAddonsMenu: false,
    setIsOpenAddonsMenu: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.isOpenAddonsMenu);
        return {
          isOpenAddonsMenu: nextValue,
          ...(nextValue
            ? {
                isUserManagementOpen: false,
                isAdminTaskListOpen: false,
                isOpenReviewerPanel: false,
              }
            : {}),
        };
      }),

    showStyleDialog: false,
    setShowStyleDialog: setField("showStyleDialog"),

    showQueryTableDialog: false,
    showQueryTableDialogRef,
    setShowQueryTableDialog: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.showQueryTableDialog);
        showQueryTableDialogRef.current = nextValue;
        return { showQueryTableDialog: nextValue };
      }),

    showCompareDialog: false,
    setShowCompareDialog: setField("showCompareDialog"),

    showLabelDialog: false,
    setShowLabelDialog: setField("showLabelDialog"),

    editFeatureDialog: null,
    setEditFeatureDialog: setField("editFeatureDialog"),

    exportDialog: false,
    setExportDialog: setField("exportDialog"),

    filterDialog: false,
    setFilterDialog: setField("filterDialog"),

    showLayersSaveDialog: false,
    setShowLayersSaveDialog: setField("showLayersSaveDialog"),

    autoLayersSave: false,
    setAutoLayersSave: setField("autoLayersSave"),

    showNewUserDialog: false,
    setShowNewUserDialog: setField("showNewUserDialog"),

    bultEditDialog: false,
    setBultEditDialog: setField("bultEditDialog"),

    layersApprovalGenerateRouteDialog: null,
    setLayersApprovalGenerateRouteDialog: setField("layersApprovalGenerateRouteDialog"),

    layersApprovalLoadLayerDialog: null,
    setLayersApprovalLoadLayerDialog: setField("layersApprovalLoadLayerDialog"),

    layersApprovalScanRouteDialog: null,
    setLayersApprovalScanRouteDialog: setField("layersApprovalScanRouteDialog"),

    showImportFromSQLDialog: null,
    setShowImportFromSQLDialog: setField("showImportFromSQLDialog"),

    showChangeSQLDialog: null,
    setShowChangeSQLDialog: setField("showChangeSQLDialog"),

    importUrls: [],
    setImportUrls: setField("importUrls"),

    featureCached: [],
    setFeatureCached: setField("featureCached"),

    cutHistory: { current: [] },

    filterGeojsonDialog: false,
    setFilterGeojsonDialog: setField("filterGeojsonDialog"),

    filterGeojsonOnAllDialog: false,
    setFilterGeojsonOnAllDialog: setField("filterGeojsonOnAllDialog"),

    // -----------------------
    // Labeling
    // -----------------------
    previewLabel: [],

    labelPropertiesOption: [],
    setLabelPropertiesOption: setField("labelPropertiesOption"),

    // -----------------------
    // Table & query
    // -----------------------
    queryTable: [],
    setQueryTable: setField("queryTable"),

    featureList: [],
    setFeatureList: setField("featureList"),

    // -----------------------
    // Delete dialog
    // -----------------------
    deleteDialog: { open: false, item: null },
    setDeleteDialog: setField("deleteDialog"),

    // -----------------------
    // Timeline
    // -----------------------
    timeLineMode: true,
    setTimeLineMode: setField("timeLineMode"),

    // -----------------------
    // Global UI
    // -----------------------
    globalMode: "none",
    setGlobalMode: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.globalMode);

        return {
          globalMode: nextValue,
          ...(nextValue === "split" ? turnOffExclusiveModesIfEnabled(true, "globalMode") : {}),
        };
      }),

    // -----------------------
    // Global overlays (non-exclusive)
    // -----------------------
    intersectionPointsEnabled: false,
    setIntersectionPointsEnabled: setField("intersectionPointsEnabled"),

    // -----------------------
    // Feature point mapping
    // -----------------------
    featurePointMapping,
    controlPointEntities,

    // -----------------------
    // Loading app
    // -----------------------
    isLoadingApp: true,
    setIsLoadingApp: setField("isLoadingApp"),

    // -----------------------
    // Cursor
    // -----------------------
    cursorMode: Type.CURSOR.DEFAULT,
    cursorModeRef,
    setCursorMode: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.cursorMode);
        cursorModeRef.current = nextValue;
        return { cursorMode: nextValue };
      }),

    // -----------------------
    // Basic geojson style
    // -----------------------
    basicGeojsonStyle: {},
    setBasicGeojsonStyle: setField("basicGeojsonStyle"),

    // -----------------------
    // Speed profile
    // -----------------------
    configSpeedProfile: {},
    setConfigSpeedProfile: setField("configSpeedProfile"),

    // -----------------------
    // Admin / operator task lists
    // -----------------------
    isAdminTaskListOpen: false,
    isOperatorTaskListOpen: false,

    setIsAdminTaskListOpen: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.isAdminTaskListOpen);
        return {
          isAdminTaskListOpen: nextValue,
          ...(nextValue
            ? {
                isUserManagementOpen: false,
                isOpenAddonsMenu: false,
                isOpenReviewerPanel: false,
              }
            : {}),
        };
      }),

    setIsOperatorTaskListOpen: setField("isOperatorTaskListOpen"),

    // -----------------------
    // Undo / redo
    // -----------------------
    undoStack: [],
    redoStack: [],
    undoStackRef,
    setUndoStack: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.undoStack);
        undoStackRef.current = nextValue;
        return { undoStack: nextValue };
      }),
    setRedoStack: setField("redoStack"),

    pushUndo: (geojsonSnapshot) =>
      set((state) => {
        const next = [...state.undoStack, geojsonSnapshot];
        undoStackRef.current = next;
        return { undoStack: next, redoStack: [] };
      }),

    pushRedo: (geojsonSnapshot) =>
      set((state) => ({
        redoStack: [...state.redoStack, geojsonSnapshot],
      })),

    popUndo: () => {
      const { undoStack } = get();
      const last = undoStack.at(-1);
      if (!last) return null;
      const next = undoStack.slice(0, -1);
      undoStackRef.current = next;
      set({ undoStack: next });
      return last;
    },

    popRedo: () => {
      const { redoStack } = get();
      const last = redoStack.at(-1);
      if (!last) return null;
      const next = redoStack.slice(0, -1);
      set({ redoStack: next });
      return last;
    },

    // -----------------------
    // Selected features
    // -----------------------
    selectedFeatures: [],
    setSelectedFeatures: setField("selectedFeatures"),

    // -----------------------
    // Camera images
    // -----------------------
    cameraImageAllAutoPalyTimeout: 500,
    setCameraImageAllAutoPalyTimeout: setField("cameraImageAllAutoPalyTimeout"),

    cameraImageAllAutoPalySkip: 1,
    setCameraImageAllAutoPalySkip: setField("cameraImageAllAutoPalySkip"),

    cameraImageAllAutoPaly: false,
    setCameraImageAllAutoPaly: setField("cameraImageAllAutoPaly"),

    // -----------------------
    // Projects
    // -----------------------
    projects: [],
    setProjects: setField("projects"),

    selectedProject: null,
    setSelectedProject: setField("selectedProject"),

    createProjectDialog: false,
    setCreateProjectDialog: setField("createProjectDialog"),

    editProjectDialog: false,
    setEditProjectDialog: setField("editProjectDialog"),

    selectedProjectForEdit: null,
    setSelectedProjectForEdit: setField("selectedProjectForEdit"),

    isShowCommentReviewDialog: false,
    setIsShowCommentReviewDialog: setField("isShowCommentReviewDialog"),

    // -----------------------
    // Direction arrows
    // -----------------------
    directionArrowSetting: directionArrowSettingRef.current,
    directionArrowSettingRef,
    setDirectionArrowSetting: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.directionArrowSetting);
        directionArrowSettingRef.current = nextValue;
        return { directionArrowSetting: nextValue };
      }),

    // -----------------------
    // Simplify tolerance
    // -----------------------
    simplifyToleranceSetting: simplifyToleranceSettingRef.current,
    simplifyToleranceSettingRef,
    setSimplifyToleranceSetting: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.simplifyToleranceSetting);
        simplifyToleranceSettingRef.current = nextValue;
        return { simplifyToleranceSetting: nextValue };
      }),

    // -----------------------
    // Misc refs
    // -----------------------
    infoboxRef: React.createRef(),

    lineSampleRate: 0,
    lineSampleRateRef,
    setLineSampleRate: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.lineSampleRate);
        lineSampleRateRef.current = nextValue;
        return { lineSampleRate: nextValue };
      }),

    // -----------------------
    // Users / queries
    // -----------------------
    users: [],
    setUsers: setField("users"),

    qeuryMenuTable: [],
    setQeuryMenuTable: setField("qeuryMenuTable"),

    showQeuryMenuTable: false,
    setShowQeuryMenuTable: setField("showQeuryMenuTable"),

    showQeuryMenu: false,
    setShowQeuryMenu: setField("showQeuryMenu"),

    showLoadLayersDialog: false,
    setShowLoadLayersDialog: setField("showLoadLayersDialog"),

    databases: [],
    setDatabases: setField("databases"),

    city: [],
    setCity: setField("city"),

    showNewTeamDialog: false,
    setShowNewTeamDialog: setField("showNewTeamDialog"),

    selectTeamForEdit: null,
    setSelectTeamForEdit: setField("selectTeamForEdit"),

    selectUserForEdit: null,
    setSelectUserForEdit: setField("selectUserForEdit"),

    teams: [],
    setTeams: setField("teams"),

    tasks: [],
    setTasks: setField("tasks"),

    selectedTask: null,
    setSelectedTask: setField("selectedTask"),

    db: "Horizon",
    setDb: setField("db"),

    defaultCity: "phoenix",
    setDefaultCity: setField("defaultCity"),

    // -----------------------
    // Infobox / highlight
    // -----------------------
    canOpenInfobox: true,
    canOpenInfoboxRef,
    setCanOpenInfobox: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.canOpenInfobox);
        canOpenInfoboxRef.current = nextValue;
        return { canOpenInfobox: nextValue };
      }),

    canHightlightEntity: true,
    canHightlightEntityRef,
    setCanHightlightEntity: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.canHightlightEntity);
        canHightlightEntityRef.current = nextValue;
        return { canHightlightEntity: nextValue };
      }),

    showMapInfo: true,
    setShowMapInfo: setField("showMapInfo"),

    showCameraInfo: false,
    setShowCameraInfo: setField("showCameraInfo"),

    showJsonEditor: false,
    setShowJsonEditor: setField("showJsonEditor"),

    // -----------------------
    // Generate arc (exclusive modes)
    // -----------------------
    generateArc: false,
    generateArcRef,
    setGenerateArc: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.generateArc));
        generateArcRef.current = nextValue;

        return {
          generateArc: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "generateArc"),
        };
      }),

    // -----------------------
    // Task / review
    // -----------------------
    currentTask: null,
    setCurrentTask: setField("currentTask"),

    reviewTitle: [],
    setReviewTitle: setField("reviewTitle"),

    // -----------------------
    // Point cloud
    // -----------------------
    isOpenPointCloudPanel: false,
    setIsOpenPointCloudPanel: setField("isOpenPointCloudPanel"),

    settings: settingsDefaultValues,
    setSettings: setField("settings"),

    // -----------------------
    // Background / copy / dialogs
    // -----------------------
    bgImageUrl: null,
    setBgImageUrl: setField("bgImageUrl"),

    isShowCopyToProjectDialog: false,
    setIsShowCopyToProjectDialog: setField("isShowCopyToProjectDialog"),

    moveToAnotherLayerDialog: { open: false, data: null },
    setMoveToAnotherLayerDialog: setField("moveToAnotherLayerDialog"),

    camera: null,
    setCamera: setField("camera"),

    isShowBackupListDialog: null,
    setIsShowBackupListDialog: setField("isShowBackupListDialog"),

    isShowDuplicateProjectDialog: null,
    setIsShowDuplicateProjectDialog: setField("isShowDuplicateProjectDialog"),

    isUserManagementOpen: false,
    setIsUserManagementOpen: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.isUserManagementOpen);
        return {
          isUserManagementOpen: nextValue,
          ...(nextValue
            ? {
                isAdminTaskListOpen: false,
                isOpenAddonsMenu: false,
                isOpenReviewerPanel: false,
              }
            : {}),
        };
      }),

    isShowMergeDialog: false,
    setIsShowMergeDialog: setField("isShowMergeDialog"),

    filterNodeMinHeight: 0,
    setFilterNodeMinHeight: setField("filterNodeMinHeight"),

    filterNodeMaxHeight: 0,
    setFilterNodeMaxHeight: setField("filterNodeMaxHeight"),

    importTypeOptions,

    // -----------------------
    // Line intersect measure (exclusive modes)
    // -----------------------
    lineIntersectMeasureMode: false,
    setLineIntersectMeasureMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.lineIntersectMeasureMode));

        return {
          lineIntersectMeasureMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "lineIntersectMeasureMode"),
        };
      }),

    // -----------------------
    // Overlay text (exclusive modes)
    // -----------------------
    overlayTextMode: false,
    setOverlayTextMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.overlayTextMode));

        return {
          overlayTextMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "overlayTextMode"),
        };
      }),

    autoSketcherMode: false,
    autoSketcherModeRef,
    setAutoSketcherMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.autoSketcherMode));
        autoSketcherModeRef.current = nextValue;

        return {
          autoSketcherMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "autoSketcherMode"),
        };
      }),

    autoSketcherDistance: 0.2,
    setAutoSketcherDistance: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.autoSketcherDistance);

        return {
          autoSketcherDistance: nextValue,
        };
      }),

    // -----------------------
    // Generate route (exclusive modes)
    // -----------------------
    generateRouteMode: false,
    generateRouteModeRef,
    setGenerateRouteMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.generateRouteMode));
        generateRouteModeRef.current = nextValue;

        return {
          generateRouteMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "generateRouteMode"),
        };
      }),

    // -----------------------
    // Traffic sign (exclusive modes)
    // -----------------------
    trafficSignMode: false,
    trafficSignModeRef,
    setTrafficSignMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.trafficSignMode));
        trafficSignModeRef.current = nextValue;

        return {
          trafficSignMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "trafficSignMode"),
        };
      }),

    // -----------------------
    // Traffic light (exclusive modes)
    // -----------------------
    trafficLightMode: false,
    trafficLightModeRef,
    setTrafficLightMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.trafficLightMode));
        trafficLightModeRef.current = nextValue;

        return {
          trafficLightMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "trafficLightMode"),
        };
      }),

    // -----------------------
    // Point cloud style
    // -----------------------
    pointCloudStyle: null,
    setPointCloudStyle: setField("pointCloudStyle"),

    // -----------------------
    // Shared tool refs
    // -----------------------
    overlayTextHandlerRef,
    highlightToolsRef,

    loadingDataForLayers,
    savedHotkeysRef,
    defaultHotkeysRef,

    // -----------------------
    // Cesium assets
    // -----------------------
    cesiumAssetsList: [],
    setCesiumAssetsList: setField("cesiumAssetsList"),

    showCloseProjectDialog: null,
    setShowCloseProjectDialog: setField("showCloseProjectDialog"),

    showDeleteProjectDialog: null,
    setShowDeleteProjectDialog: setField("showDeleteProjectDialog"),

    showSwitchLayerDialog: false,
    setShowSwitchLayerDialog: setField("showSwitchLayerDialog"),

    czmlChart: null,
    setCzmlChart: setField("czmlChart"),

    // -----------------------
    // Editing permission
    // -----------------------
    userCanEditing: false,
    userCanEditingRef: { current: false },
    setUserCanEditing: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.userCanEditing);
        get().userCanEditingRef.current = nextValue;
        return { userCanEditing: nextValue };
      }),

    showApprovalEditDialog: false,
    setShowApprovalEditDialog: setField("showApprovalEditDialog"),

    showLoadingMap: false,

    // -----------------------
    // Fast association (exclusive modes)
    // -----------------------
    fastAssociation: false,
    setFastAssociation: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.fastAssociation));
        return {
          fastAssociation: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "fastAssociation"),
        };
      }),

    // -----------------------
    // Loaded projects / saved props
    // -----------------------
    loadedPorject: [],
    setLoadedPorject: setField("loadedPorject"),

    savedPropsInNewEntity: {},
    setSavedPropsInNewEntity: setField("savedPropsInNewEntity"),

    savedPropsInNewEntityLayerKey: null,
    setSavedPropsInNewEntityLayerKey: (key) => set({ savedPropsInNewEntityLayerKey: key }),

    showRoadSlopeDialog: null,
    setShowRoadSlopeDialog: setField("showRoadSlopeDialog"),

    showZigzagDialog: null,
    setShowZigzagDialog: setField("showZigzagDialog"),

    // -----------------------
    // Global undo stack actions
    // -----------------------
    globalUndoStackRef,
    pushUndoAction: (action) => {
      globalUndoStackRef.current.push(action);
    },
    popUndoAction: () => {
      const arr = globalUndoStackRef.current;
      if (!arr.length) return null;
      return arr.pop() || null;
    },

    // -----------------------
    // Layer tab
    // -----------------------
    layerTab: "projects",
    setLayerTab: setField("layerTab"),

    // -----------------------
    // Road marking modes (exclusive)
    // -----------------------
    roadMarkingMode: false,
    roadMarkingModeRef,
    setRoadMarkingMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.roadMarkingMode));
        roadMarkingModeRef.current = nextValue;

        return {
          roadMarkingMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "roadMarkingMode"),
        };
      }),

    roadMarkingMovingMode: false,
    roadMarkingMovingModeRef,
    setRoadMarkingMovingMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.roadMarkingMovingMode));
        roadMarkingMovingModeRef.current = nextValue;

        return {
          roadMarkingMovingMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "roadMarkingMovingMode"),
        };
      }),

    toolsEnabled: initialToolsEnabled,

    setToolEnabled: (id, value) =>
      set((state) => {
        const next = { ...(state.toolsEnabled || {}), [id]: value };
        try {
          localStorage.setItem("toolsEnabled", JSON.stringify(next));
        } catch {}
        return { toolsEnabled: next };
      }),

    // -----------------------
    // Turning Arc (exclusive mode)
    // -----------------------
    turningArcMode: false,
    turningArcModeRef: { current: false },

    setTurningArcMode: (updater) =>
      set((state) => {
        const nextValue = resolveUpdater(updater, state.turningArcMode);
        get().turningArcModeRef.current = Boolean(nextValue);

        return {
          turningArcMode: Boolean(nextValue),
          ...turnOffExclusiveModesIfEnabled(nextValue, "turningArcMode"),
        };
      }),

    drivewayMode: false,
    drivewayModeRef,
    setDrivewayMode: (updater) =>
      set((state) => {
        const nextValue = Boolean(resolveUpdater(updater, state.drivewayMode));
        drivewayModeRef.current = nextValue;

        return {
          drivewayMode: nextValue,
          ...turnOffExclusiveModesIfEnabled(nextValue, "drivewayMode"),
        };
      }),

    laneConnectorMode: false,
    setLaneConnectorMode: (v) => set({ laneConnectorMode: v }),
  };
});
